import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DictionaryItem, DictionaryItemListing, DictionaryType, DictionaryTypeLabels} from '../types/DictionaryItem';
import dictionaryItemsService from '../services/dictionaryItemsService';
import {DictionaryItemsForm} from './DictionaryItemsForm';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import AdminFormHeader from '../../../layout/admin/AdminFormHeader';
import {AdminButton} from '../../../layout/admin';
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";
import {useToast} from "../../../common/contexts/ToastContext";

interface Props {
  dictionaryType: DictionaryType;
}

export const DictionaryItemsList = ({dictionaryType}: Props) => {
  const {t} = useTranslation();
  const {showError} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [items, setItems] = useState<DictionaryItemListing[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState<DictionaryItem | undefined>(undefined);

  const loadItems = async () => {
    try {
      const data = await dictionaryItemsService.getDictionaryItemsByType(dictionaryType);
      setItems(data);
    } catch (error) {
      console.error('Error loading dictionary items:', error);
    }
  };

  useEffect(() => {
    loadItems();
  }, [dictionaryType]);

  const handleCreate = () => {
    setEditingItem(undefined);
    setIsEditing(true);
  };

  const handleEdit = async (alias: string) => {
    try {
      const item = await dictionaryItemsService.getDictionaryItem(dictionaryType, alias);
      setEditingItem(item);
      setIsEditing(true);
    } catch (error) {
      console.error('Error loading item for edit:', error);
    }
  };

  const handleDelete = async (alias: string) => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDelete'),
      accept: async () => {
        try {
          await dictionaryItemsService.deleteDictionaryItem(dictionaryType, alias);
          loadItems();
        } catch (error) {
          console.error('Error deleting item:', error);
        }
      }
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        await dictionaryItemsService.updateDictionaryItem(dictionaryType, editingItem.alias, values);
      } else {
        await dictionaryItemsService.createDictionaryItem(dictionaryType, values);
      }
      setIsEditing(false);
      loadItems();
    } catch (error) {
      console.error('Error saving item:', error);
      throw error;
    }
  };

  if (isEditing) {
    return (
      <DictionaryItemsForm
        dictionaryType={dictionaryType}
        initialValues={editingItem}
        onSubmit={handleSubmit}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <>
      <AdminFormHeader
        title={DictionaryTypeLabels[dictionaryType]}
        subtitle={t('admin.core.dictionaryItems.subtitle.list')}
      />

      <div className="mb-4">
        <AdminButton
          label={t('admin.core.dictionaryItems.actions.createNew')}
          onClick={handleCreate}
          variant="black"
        />
      </div>

      <DataTable value={items} stripedRows>
        <Column
          field="value"
          header={t('admin.core.dictionaryItems.columns.value')}
          sortable
          body={(item: DictionaryItemListing) => (
            <strong>{item.value}</strong>
          )}
        />
        <Column
          field="alias"
          header={t('admin.core.dictionaryItems.columns.alias')}
          sortable
          body={(item: DictionaryItemListing) => (
            <code>{item.alias}</code>
          )}
        />
        <Column
          header={t('admin.common.columns.actions')}
          body={(item: DictionaryItemListing) => (
            <div>
              <Button
                onClick={() => handleEdit(item.alias)}
                icon="fa fa-edit"
                severity="info"
                size="small"
                outlined
                className="mr-2"
                tooltip={t('admin.common.actions.edit')}
                tooltipOptions={{position: 'top'}}
              />
              <Button
                onClick={() => handleDelete(item.alias)}
                icon="fa fa-trash"
                severity="danger"
                size="small"
                outlined
                tooltip={t('admin.common.actions.delete')}
                tooltipOptions={{position: 'top'}}
              />
            </div>
          )}
        />
      </DataTable>
    </>
  );
};

export default DictionaryItemsList;
